import {type ApolloError, type ApolloQueryResult} from 'apollo-client';
import React, {createContext, useContext, useMemo} from 'react';

import {
  type HomePageViewerDetailsQuery,
  useHomePageViewerDetailsQuery,
} from '../../generated/graphql';
import {type HomeViewerDetails} from '../../graphql/homePage';
import {type Override} from '../../graphql/teamQuery';

export type HomePageContextType = {
  homePageViewerDetailsQuery: {
    data?: HomeViewerDetails;
    isLoading: boolean;
    refetch: () => Promise<ApolloQueryResult<HomePageViewerDetailsQuery>>;
    error?: ApolloError;
  };
};

export const HomePageContext = createContext<HomePageContextType | undefined>(
  undefined
);

type HomePageContextProviderProps = {
  children: React.ReactNode;
};

export const HomePageContextProvider = ({
  children,
}: HomePageContextProviderProps) => {
  const homePageViewerDetailsQuery = useHomePageViewerDetailsQuery();

  const homePageViewer = homePageViewerDetailsQuery?.data?.viewer;

  const values = useMemo(
    () => ({
      homePageViewerDetailsQuery: {
        data: homePageViewer as Override<
          typeof homePageViewer,
          HomeViewerDetails
        >,
        isLoading: homePageViewerDetailsQuery?.loading,
        refetch: homePageViewerDetailsQuery?.refetch,
        error: homePageViewerDetailsQuery?.error,
      },
    }),
    [homePageViewer, homePageViewerDetailsQuery]
  );

  return (
    <HomePageContext.Provider value={values}>
      {children}
    </HomePageContext.Provider>
  );
};

export function useHomePageContext(): HomePageContextType {
  const value = useContext(HomePageContext);

  if (value == null) {
    throw new Error(
      'useHomePageContext must be used within a HomePageContextProvider'
    );
  }

  return value;
}
